import { Grid } from "@mui/material"
import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { customPalette } from "../../../../../../../theme"
import { IconCardInfo } from "../../../../../../components/custom-icons/icon-card-info"
import { IconParcelleProject } from "../../../../../../components/custom-icons/icon-parcelle-project"
import { ConfirmationDialog } from "../../../../../../components/dialog/ConfirmationDialog"
import { TabEnum } from "../../../../../../components/tabs/tabs"
import { BSVariantContext } from "../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { TabPanel } from "../building/BSCalculationPage/BSCalculationForm/components/TabPanel"
import { BSLevelContext } from "../building/BSCreateBuildingPages/BSZoneAndLevelPage/context/BSLevelContext"
import { BSBuildingTabMenu } from "../building/components/BSBuildingTabMenu"
import { BSBuildingContext } from "../building/components/context/BSBuildingContext"
import { BSBuildingDashboard } from "../tabs/BSBuildingTabs/BSBuildingDashboardTab/BSBuildingDashboard"
import { BSBuildingFondationTab } from "../tabs/BSBuildingTabs/BSBuildingFondationTab/BSBuildingFondationTab"
import { BSBuildingInfosTab } from "../tabs/BSBuildingTabs/BSBuildingInfosTab/BSBuildingInfosTab"
import { BSBuildingMorphoTab } from "../tabs/BSBuildingTabs/BSBuildingMorphologyTab/BSBuildingMorphoTab"
import { BSParkingTab } from "../tabs/BSBuildingTabs/BSBuildingParkingTab/BSParkingTab"
import { BSBuildingZoneAndLevelTab } from "../tabs/BSBuildingTabs/BSBuildingZoneAndLevelTab/BSBuildingZoneAndLevelTab"
import { BSPatioTab } from "../tabs/BSBuildingTabs/BSPatioTab/BSPatioTab"
import { BSVariantInfosTab } from "../tabs/BSVariantInfosTab/BSVariantInfos"
import { BSVariantParcelleTab } from "../tabs/BSVariantParcelleTab/BSVariantParcelleTab"
import { BSVariantControlPanelContext, BSVariantControlPanelProvider } from "./BSVariantControlPanelContext"
import { ControlTabPanel } from "./ControlPanelTab"

export function BSVariantControlPanel(): React.JSX.Element {
  return (
    <BSVariantControlPanelProvider>
      <BSVariantControlPanelCore />
    </BSVariantControlPanelProvider>
  )
}

function BSVariantControlPanelCore(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const { selectedVariant } = useContext(BSVariantContext)
  const { controlPanelRef } = useContext(BSVariantControlPanelContext)
  const { selectBSBuilding, selectedBSBuilding } = useContext(BSBuildingContext)
  const { refreshBuildingLevel } = useContext(BSLevelContext)

  const [selectedTab, setSelectedTab] = useState<TabEnum>(TabEnum.BS_VARIANT_INFO)
  const tmpSelectedTab = useRef(TabEnum.BS_VARIANT_INFO)
  const [openWarningDialog, setOpenWarningDialog] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  useEffect(() => {
    const tab = searchParams.get("tab")
    if (tab) {
      setSelectedTab(Number(tab))
    }
  }, [searchParams])

  const setUrl: (index: TabEnum, bsBuildingId?: string) => void = useCallback(
    (index, bsBuildingId) => {
      const params = new URLSearchParams(location.search)

      if (
        index === TabEnum.BS_VARIANT_PARCELLE ||
        index === TabEnum.BS_VARIANT_INFO ||
        index === TabEnum.BS_VARIANT_DASHBOARD
      ) {
        params.delete("bsBuildingId")
      } else if (selectedBSBuilding || bsBuildingId) {
        if (selectedBSBuilding) {
          params.set("bsBuildingId", selectedBSBuilding.id)
        } else if (bsBuildingId) {
          params.set("bsBuildingId", bsBuildingId)
        }
      }
      params.delete("tab")
      params.set("tab", index.toString())

      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true }
      )
    },
    [location.pathname, location.search, navigate, selectedBSBuilding]
  )

  const handleSelectTab: (index: TabEnum, bsBuildingId?: string) => void = useCallback(
    (index, bsBuildingId) => {
      if (index === selectedTab) {
        return
      }
      if (isUpdating) {
        setOpenWarningDialog(true)
        tmpSelectedTab.current = index
        return
      }
      setSelectedTab(index)

      setUrl(index, bsBuildingId)
    },
    [isUpdating, selectedTab, setUrl]
  )

  return (
    <Grid container height="100%" pt={1}>
      <Grid
        item
        ref={controlPanelRef}
        border={4}
        xs={3}
        display="flex"
        flexDirection="column"
        height="100%"
        borderColor={customPalette.lightGray7}
        borderRadius={5}
        p={1}
        gap={1}>
        <ControlTabPanel
          title="Informations projet"
          icon={IconCardInfo}
          isSelectedTab={selectedTab === TabEnum.BS_VARIANT_INFO}
          onClick={() => {
            selectBSBuilding("")
            handleSelectTab(TabEnum.BS_VARIANT_INFO)
          }}
        />

        <ControlTabPanel
          title="Parcelle"
          icon={IconParcelleProject}
          isSelectedTab={selectedTab === TabEnum.BS_VARIANT_PARCELLE}
          onClick={() => {
            selectBSBuilding("")
            handleSelectTab(TabEnum.BS_VARIANT_PARCELLE)
          }}
        />

        <BSBuildingTabMenu handleSelectTab={handleSelectTab} selectedTab={selectedTab} />
      </Grid>

      {/* For now we keep tab panel because it allows to keep components mounted */}
      {/* If we see that its becoming slow, we can adjust with a switch */}
      <Grid item xs={9} height="100%">
        {/* Main section */}
        <TabPanel selectedTab={selectedTab} index={TabEnum.BS_VARIANT_DASHBOARD} />
        <TabPanel selectedTab={selectedTab} index={TabEnum.BS_VARIANT_INFO}>
          <BSVariantInfosTab bsVariant={selectedVariant} setIsUpdating={setIsUpdating} isUpdating={isUpdating} />
        </TabPanel>
        <TabPanel selectedTab={selectedTab} index={TabEnum.BS_VARIANT_PARCELLE}>
          <BSVariantParcelleTab bsVariant={selectedVariant} />
        </TabPanel>

        {/* Building section */}
        <TabPanel selectedTab={selectedTab} index={TabEnum.BUILDING_DASHBOARD}>
          <BSBuildingDashboard />
        </TabPanel>
        <TabPanel selectedTab={selectedTab} index={TabEnum.BUILDING_INFO}>
          <BSBuildingInfosTab setSelectedTab={setSelectedTab} setIsUpdating={setIsUpdating} isUpdating={isUpdating} />
        </TabPanel>
        <TabPanel selectedTab={selectedTab} index={TabEnum.BUILDING_MORPHOLOGY_OR_BIM}>
          <BSBuildingMorphoTab isUpdating={isUpdating} setIsUpdating={setIsUpdating} />
        </TabPanel>
        <TabPanel selectedTab={selectedTab} index={TabEnum.BUILDING_LEVEL}>
          <BSBuildingZoneAndLevelTab setSelectedTab={setSelectedTab} setIsUpdating={setIsUpdating} />
        </TabPanel>
        <TabPanel selectedTab={selectedTab} index={TabEnum.BUILDING_PATIO}>
          <BSPatioTab setIsUpdating={setIsUpdating} />
        </TabPanel>
        <TabPanel selectedTab={selectedTab} index={TabEnum.BUILDING_FONDATIONS}>
          <BSBuildingFondationTab isUpdating={isUpdating} setIsUpdating={setIsUpdating} />
        </TabPanel>
        <TabPanel selectedTab={selectedTab} index={TabEnum.BUILDING_PARKING}>
          <BSParkingTab isUpdating={isUpdating} setIsUpdating={setIsUpdating} />
        </TabPanel>
      </Grid>

      <ConfirmationDialog
        id=""
        title="Attention"
        contentText="Les informations n'ont pas été enregistrés. Êtes vous sûr de vouloir continuer ?"
        handleClose={() => setOpenWarningDialog(false)}
        action={() => {
          const buildingId = searchParams.get("bsBuildingId") ?? undefined

          if (selectedTab === TabEnum.BUILDING_LEVEL) {
            refreshBuildingLevel()
          }
          setIsUpdating(false)
          setSelectedTab(tmpSelectedTab.current)
          setUrl(tmpSelectedTab.current, buildingId)

          return Promise.resolve()
        }}
        open={openWarningDialog}
      />
    </Grid>
  )
}
